// Read-only aircraft-detail values. Missing reports must never become valid zeroes.
export const DETAIL_MISSING = '-'
export function detailNumber (value) {
  if ((typeof value !== 'number' && typeof value !== 'string') || String(value).trim() === '') return undefined
  const number = Number(value)
  return Number.isFinite(number) ? number : undefined
}
export function detailValue (value, unit = '', scale = 1, digits) {
  const number = detailNumber(value)
  if (number === undefined) return DETAIL_MISSING
  return `${digits === undefined ? number / scale : (number / scale).toFixed(digits)}${unit}`
}
export function detailEnum (value, labels) {
  const number = detailNumber(value)
  return number === undefined ? DETAIL_MISSING : labels[number] ?? `未知（${number}）`
}
export function flightDuration (value) {
  const number = detailNumber(value)
  if (number === undefined || number < 0) return DETAIL_MISSING
  const seconds = Math.floor(number)
  if (seconds < 60) return `${seconds}秒`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}分钟`
  return `${Math.floor(seconds / 3600)}小时${Math.floor(seconds % 3600 / 60)}分钟`
}
export function aircraftBattery (battery) {
  // Explicit empty list means removed; never fall back to the preceding battery.
  return (Array.isArray(battery?.batteries) ? battery.batteries : [])
    .filter(item => item && typeof item === 'object')
    .sort((a, b) => (detailNumber(a.index) ?? Infinity) - (detailNumber(b.index) ?? Infinity))[0] || {}
}
export const batteryStoreMode = value => detailEnum(value, { 1: '计划', 2: '待命' })
export const rthMode = value => detailEnum(value, { 0: '智能高度', 1: '设定高度' })
export function positionSummary (position = {}) {
  const fixed = detailEnum(position?.is_fixed, { 0: '未开始', 1: '收敛中', 2: '收敛成功', 3: '收敛失败' })
  const quality = detailEnum(position?.quality, { 1: '1档', 2: '2档', 3: '3档', 4: '4档', 5: '5档', 10: 'RTK fixed' })
  const missingCounts = detailNumber(position?.rtk_number) === undefined && detailNumber(position?.gps_number) === undefined
  return { counts: missingCounts ? DETAIL_MISSING : `RTK ${detailValue(position?.rtk_number)} / GPS ${detailValue(position?.gps_number)}`, detail: fixed === DETAIL_MISSING && quality === DETAIL_MISSING ? '' : `${fixed} · ${quality}` }
}
export function wirelessSummary (link = {}) {
  const connected = value => value === true || value === 1 ? '已连接' : value === false || value === 0 ? '未连接' : DETAIL_MISSING
  const quality = value => {
    const n = detailNumber(value)
    return n === undefined ? DETAIL_MISSING : n >= 0 && n <= 5 ? `${n}/5` : `未知（${n}）`
  }
  return {
    mode: detailEnum(link?.link_workmode, { 0: 'SDR模式', 1: '4G融合模式' }),
    rows: [
      { label: 'SDR连接', value: connected(link?.sdr_link_state) }, { label: 'SDR质量', value: quality(link?.sdr_quality) },
      { label: '4G连接', value: connected(link?.['4g_link_state']) }, { label: '4G总体质量', value: quality(link?.['4g_quality']) },
      { label: '机场侧4G质量', value: quality(link?.['4g_gnd_quality']) }, { label: '飞行器侧4G质量', value: quality(link?.['4g_uav_quality']) },
      { label: '飞行器通信模块数', value: detailValue(link?.dongle_number) },
      { label: 'SDR频段（原始值）', value: detailValue(link?.sdr_freq_band) }, { label: '4G频段（原始值）', value: detailValue(link?.['4g_freq_band']) }
    ]
  }
}
const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)
export function mergeAircraftTelemetry (previous = {}, report = {}, receivedAt = Date.now(), telemetry = true) {
  const result = { ...previous, ...report, _detail_times: { ...previous._detail_times }, _detail_field_times: { ...previous._detail_field_times } }
  const stamp = (node, prefix = '') => {
    for (const [key, value] of Object.entries(node || {})) {
      if (key.startsWith('_detail_')) continue
      const path = prefix ? `${prefix}.${key}` : key
      if (isObject(value)) stamp(value, path)
      else if (value !== undefined && value !== null) result._detail_field_times[path] = receivedAt
    }
  }
  stamp(report)
  for (const [field, time] of [['rth_mode', 'rth_mode_at'], ['current_rth_mode', 'current_rth_mode_at']]) {
    if ((previous[time] ?? previous.rth_state_at ?? 0) > (report[time] ?? report.rth_state_at ?? 0)) {
      if (previous[field] !== undefined) result[field] = previous[field]
      if (previous[time] !== undefined) result[time] = previous[time]
      result._detail_field_times[field] = previous._detail_field_times?.[field]
    }
  }
  if (previous.rth_state_at || report.rth_state_at) result.rth_state_at = Math.max(previous.rth_state_at || 0, report.rth_state_at || 0)
  for (const key of Object.keys(report)) {
    if (key.startsWith('_detail_')) continue
    if (isObject(report[key])) result[key] = { ...(previous[key] || {}), ...report[key] }
    result._detail_times[key] = receivedAt
  }
  const oldBattery = aircraftBattery(previous.battery)
  const nextBattery = aircraftBattery(report.battery)
  const stampBattery = () => {
    for (const [key, value] of Object.entries(nextBattery)) {
      if (value !== undefined && value !== null) result._detail_field_times[`battery.batteries.${key}`] = receivedAt
    }
  }
  stampBattery()
  if (Array.isArray(report.battery?.batteries) && (!report.battery.batteries.length ||
      (nextBattery.sn && oldBattery.sn && nextBattery.sn !== oldBattery.sn) ||
      (nextBattery.index !== undefined && oldBattery.index !== undefined && nextBattery.index !== oldBattery.index))) {
    result.battery = { ...report.battery }
    for (const key of Object.keys(result._detail_field_times)) if (key.startsWith('battery.')) delete result._detail_field_times[key]
    stamp({ battery: report.battery })
    stampBattery()
  }
  // State packets cannot establish or refresh telemetry freshness.
  result._detail_received_at = telemetry && report.mode_code !== undefined ? receivedAt : previous._detail_received_at
  return result
}
export function mergeDockTelemetry (previous = {}, report = {}, receivedAt = Date.now()) {
  const result = { ...previous }
  if (report.mode_code !== undefined) {
    result.basic_osd = { ...previous.basic_osd, ...report }
    result._detail_basic_at = receivedAt
  }
  if (report.wireless_link !== undefined || report.drc_state !== undefined) {
    result.link_osd = { ...previous.link_osd, ...report }
    const paired = report.wireless_link_aircraft_sn ?? report.sub_device?.device_sn ?? result.basic_osd?.sub_device?.device_sn
    if (isObject(report.wireless_link)) result.link_osd.wireless_link = { ...(paired && paired === previous._detail_link_aircraft_sn ? previous.link_osd?.wireless_link : {}), ...report.wireless_link }
    if (report.wireless_link !== undefined) {
      result._detail_wireless_times = paired && paired === previous._detail_link_aircraft_sn ? { ...previous._detail_wireless_times } : {}
      for (const key of Object.keys(report.wireless_link || {})) result._detail_wireless_times[key] = report.wireless_link_reported_at ?? receivedAt
      result._detail_link_at = report.wireless_link_reported_at ?? receivedAt
      // Bind a saved link sample to the paired aircraft at that moment.
      result._detail_link_aircraft_sn = report.wireless_link_aircraft_sn ?? report.sub_device?.device_sn ?? result.basic_osd?.sub_device?.device_sn
    }
  }
  if (report.battery_store_mode !== undefined) {
    result.basic_osd = { ...result.basic_osd, battery_store_mode: report.battery_store_mode }
    result._detail_battery_mode_at = report.battery_store_mode_reported_at ?? receivedAt
  }
  if (report.job_number !== undefined) result.work_osd = { ...previous.work_osd, ...report }
  return result
}
